import React, { useEffect, useState } from "react"
import { Dialog, DialogSurface, Spinner } from "@fluentui/react-components"
import { useAppContext } from "@/app/AppContext"
import { AuthCheckResponse } from "@/models/models";
import { AuthRoles, hasRole } from "@/auth/AuthRoles";
import NotificationDialog from "@/components/NotificationDialog";
import { stripBasePath, withBasePath } from "@/utils/basePath";

export interface ViewProps {
    title: string;
    url: string;
    /** @deprecated Use auth_roles instead */
    auth_required?: boolean;
    /**
     * Roles allowed to access this view.
     * - [AuthRoles.PUBLIC]: No authentication required
     * - [AuthRoles.ANY]: Any authenticated user (default if not specified)
     * - [AuthRoles.ADMIN]: Admin only
     * - [AuthRoles.ADMIN, AuthRoles.SUPERVISOR]: Either role
     */
    auth_roles?: string[];
    params?: string[]; // Array of parameter names this view accepts
};

// Interface for route parameters
interface RouteParams {
    [key: string]: string;
}

// Recursive type for the view tree structure
interface ViewTree {
    [key: string]: string | ViewTree;
}

const views = import.meta.glob('/src/views/**/*.tsx')

export const AppRouter: React.FC = () => {
    const [ ViewComponent, setViewComponent ] = useState<React.ComponentType | null>(null);
    const [ loadState, setLoadState ] = useState<boolean | string>(true);
    const [ viewTree, setViewTree ] = useState<ViewTree>({});
    const [ routeParams, setRouteParams ] = useState<RouteParams>({});
    const { user, setUser, setAccessToken, currentPath, setCurrentPath, dialog, setDialog, setActionButtons } = useAppContext();

    useEffect(() => {
        // On initial mount, check if the user is authenticated
        (async () => {
            setLoadState(true);
            try {
                const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/check`, {
                    credentials: 'include'
                });
                const resJson = await res.json() as AuthCheckResponse;
                if (resJson) {
                    setUser(resJson.user);
                    setAccessToken(resJson.access_token);
                    // Authenticated – go to home unless already at another route
                    let targetPath = stripBasePath(window.location.pathname);
                    const queryString = window.location.search;
                    if (targetPath === '/' || targetPath === '/login') {
                        targetPath = '/home';
                    }
                    window.history.replaceState({}, '', withBasePath(targetPath) + queryString);
                    setCurrentPath(targetPath + queryString);
                } else {
                    window.history.replaceState({}, '', withBasePath('/login'));
                    setCurrentPath('/login');
                }
            } catch (e) {
                window.history.replaceState({}, '', withBasePath('/login'));
                setCurrentPath('/login');
            }
        })();

        // Build a recursive tree of views
        let newViewTree: ViewTree = {};
        for (const view of Object.keys(views)) {
            // Strip the /src/views/ and .tsx from the view path
            const viewPath = view.replace(/^\/src\/views\//, '').replace(/\.tsx$/, '');
            const pathParts = viewPath.split('/');
            
            // Build the tree recursively
            let currentLevel = newViewTree;
            for (let i = 0; i < pathParts.length; i++) {
                const part = pathParts[i].toLowerCase();
                
                if (i === pathParts.length - 1) {
                    // This is the file name (leaf node)
                    currentLevel[part] = view;
                } else {
                    // This is a directory (branch node)
                    if (!currentLevel[part]) {
                        currentLevel[part] = {};
                    }
                    currentLevel = currentLevel[part] as ViewTree;
                }
            }
        }
        setViewTree(newViewTree);
        
        //Handle back button
        window.addEventListener('popstate', (event) => {
            setCurrentPath(stripBasePath(window.location.pathname));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (currentPath === null || Object.keys(viewTree).length === 0) {
            return;
        }
        
        // Find the view in the tree and extract parameters
        const { viewPath, remainingParts } = findViewInTree(currentPath);
        
        if (viewPath) {
            setLoadState(true);
            setActionButtons([]);
            loadView(viewPath, remainingParts);
        } else {
            setLoadState(`Path ${currentPath} not found`);
        }
        //Push the current path to the browser history
        window.history.pushState({}, '', withBasePath(currentPath));
    }, [currentPath, viewTree])

    const loadView = async (path: string, remainingParts: string[]) => {
        const view = await views[path]() as any;
        const props = view.viewProps as ViewProps;

        // Determine authorization requirements
        // Priority: auth_roles > auth_required (deprecated) > default (ANY authenticated)
        let requiredRoles: string[];
        if (props?.auth_roles) {
            requiredRoles = props.auth_roles;
        } else if (props?.auth_required === false) {
            requiredRoles = [AuthRoles.PUBLIC];
        } else {
            // Default: any authenticated user
            requiredRoles = [AuthRoles.ANY];
        }

        const isPublic = requiredRoles.includes(AuthRoles.PUBLIC);

        // Check authentication
        if (!isPublic && !user) {
            // Redirect unauthenticated users to login
            window.history.replaceState({}, '', withBasePath('/login'));
            setCurrentPath('/login');
            setLoadState(false);
            return;
        }

        // Check role authorization (if not public and not ANY)
        if (!isPublic && !requiredRoles.includes(AuthRoles.ANY)) {
            const userRoles = user?.roles ?? [];
            if (!hasRole(userRoles, requiredRoles)) {
                // User doesn't have required role - show forbidden or redirect
                setLoadState('You do not have permission to access this page');
                return;
            }
        }

        // Map remaining path parts to named parameters
        const params: RouteParams = {};
        if (props?.params && remainingParts.length > 0) {
            props.params.forEach((paramName, index) => {
                if (remainingParts[index]) {
                    params[paramName] = remainingParts[index];
                }
            });
        }

        // Merge query parameters (path params take precedence)
        const queryString = currentPath.split('?')[1] || window.location.search.replace('?', '');
        if (queryString) {
            const searchParams = new URLSearchParams(queryString);
            searchParams.forEach((value, key) => {
                if (!(key in params)) {
                    params[key] = value;
                }
            });
        }
        
        setRouteParams(params);

        // If we reach this point the user is allowed to access the route.
        // Create a component that passes the parameters as props
        const ComponentWithParams = () => {
            const Component = view.default;
            return <Component {...params} />;
        };
        
        setViewComponent(() => ComponentWithParams);
        if (props) {
            document.title = props.title;
        }
        setLoadState(false);
    }

    // Function to find a view in the tree and extract parameters
    const findViewInTree = (path: string): { viewPath: string | null; remainingParts: string[] } => {
        // Strip query string before processing
        const pathWithoutQuery = path.split('?')[0];
        const pathParts = pathWithoutQuery.split('/').filter(part => part.length > 0);
        let currentLevel = viewTree;
        let viewPath: string | null = null;
        let remainingParts: string[] = [];

        for (let i = 0; i < pathParts.length; i++) {
            const part = pathParts[i].toLowerCase();

            if (currentLevel[part] && typeof currentLevel[part] === 'string') {
                // Found a view file
                viewPath = currentLevel[part] as string;

                // Get remaining path parts that could be parameters
                remainingParts = pathParts.slice(i + 1);
                break;
            } else if (currentLevel[part] && typeof currentLevel[part] === 'object') {
                // Continue traversing the tree
                currentLevel = currentLevel[part] as ViewTree;

                // If this is the last part and there's an index file, use it
                if (i === pathParts.length - 1 && currentLevel['index'] && typeof currentLevel['index'] === 'string') {
                    viewPath = currentLevel['index'] as string;
                    remainingParts = [];
                    break;
                }
            } else {
                // Path not found
                break;
            }
        }

        return { viewPath, remainingParts };
    };

    return (
        <>
            { loadState === true ? <Spinner size="huge" /> : 
              typeof loadState === 'string' ? <div>{loadState}</div> : 
              ViewComponent ? <ViewComponent {...routeParams} /> : null }
            { dialog && <Dialog modalType="alert" open={true} onOpenChange={(_event,data) => !data.open ? setDialog(null) : null}>
                <DialogSurface>
                    {dialog}
                </DialogSurface>
            </Dialog>
            }
            <NotificationDialog />
        </>
    )
}